// Shared Knowledge Base UI helpers (Session AFA), mirroring the environmentalUi pattern: the pills,
// the site/lift/manufacturer/model context fields, the tag editor (used by the list modal and the
// detail page's inline form), and the two-stage soft-then-hard delete flow (used by the list row and
// the detail page). Kept in one place so the list and the detail page cannot drift.
//
// Session AFD adds the query-answer chrome (expandable source cards, inline citation markers, the
// web-search notice and web source cards, the needs-context prompt and the answer-source pill). The
// pure logic behind all of it lives in src/lib/knowledgeTypes.ts and is shared with the engineer
// Fault Assistant, so the two shells cannot disagree about what a citation or a match strength means.
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import "./knowledgeUi.css";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { Button } from "../../components/Button";
import { StatusPill } from "../../components/StatusPill";
import { KnowledgePageViewer } from "../../components/KnowledgePageViewer";
import { RecordPicker } from "../../components/RecordPicker";
import {
  DOC_TYPES,
  DOC_TYPE_LABEL,
  WEB_SEARCH_NOTICE,
  WEB_SEARCH_NO_SOURCES_NOTICE,
  docTypePill,
  docTypeLabel,
  matchStrength,
  matchStrengthLabel,
  matchStrengthPill,
  tierLabel,
  tierPill,
  parseAnswerSegments,
  type KnowledgeDocType,
  type KnowledgeDocument,
  type KnowledgeQuerySource,
  type KnowledgeTagInput,
  type KnowledgeTagsPatchInput,
  type KnowledgeWebSource,
} from "../../lib/knowledgeTypes";

export const inputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green max-md:min-h-11";
export const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

// ---- Pills ----
// The document type, the ingest status and the answer source are StatusPill at their call sites
// (design system Session 7a): a type is the blue pill throughout (docTypeLabel), an ingest status is
// <StatusPill domain="knowledgeIngest" ...> (the Director-specified tones), and an answer source takes
// answerSourcePill's tone with answerSourceLabel's words.

// ---- Context fields (site, lift, manufacturer, model) ----
// The four tags an office user sets by hand, shared by the upload/pull modals, the tag editor and the
// query context. Lift options filter to the chosen site; changing the site clears a lift that no
// longer belongs to it.

export interface ContextValue {
  siteId: string;
  assetId: string;
  manufacturer: string;
  liftModel: string;
}

export const emptyContext: ContextValue = { siteId: "", assetId: "", manufacturer: "", liftModel: "" };

// Convert the context fields to the tag input the ingest endpoints take: a blank field is omitted.
export function contextToTags(ctx: ContextValue): KnowledgeTagInput {
  return {
    siteId: ctx.siteId || undefined,
    assetId: ctx.assetId || undefined,
    manufacturer: ctx.manufacturer.trim() || undefined,
    liftModel: ctx.liftModel.trim() || undefined,
  };
}

export function ContextFields({
  value,
  onChange,
  idPrefix,
}: {
  value: ContextValue;
  onChange: (next: ContextValue) => void;
  idPrefix: string;
}) {
  // Session SC2A: the site and the lift are typeahead pickers (the lift narrowed to the chosen site), never preloaded registers.
  function setSite(siteId: string) {
    // Clear a lift: it belongs to the previously chosen site, and the lift picker is now narrowed to the new one.
    onChange({ ...value, siteId, assetId: siteId === value.siteId ? value.assetId : "" });
  }

  return (
    <>
      <div>
        <RecordPicker kind="site" id={`${idPrefix}-site`} label="Site" value={value.siteId} placeholder="Any site (search to choose)" onChange={(id) => setSite(id)} />
      </div>
      <div>
        <RecordPicker
          kind="lift"
          id={`${idPrefix}-lift`}
          label="Lift"
          value={value.assetId}
          placeholder="Any lift (search to choose)"
          filters={value.siteId ? { siteId: value.siteId } : undefined}
          onChange={(id) => onChange({ ...value, assetId: id })}
        />
      </div>
      <label>
        <span className={labelCls}>Manufacturer</span>
        <input
          id={`${idPrefix}-manufacturer`}
          aria-label="Manufacturer"
          value={value.manufacturer}
          onChange={(e) => onChange({ ...value, manufacturer: e.target.value })}
          placeholder="e.g. Kone, Otis"
          className={inputCls}
        />
      </label>
      <label>
        <span className={labelCls}>Model</span>
        <input
          id={`${idPrefix}-model`}
          aria-label="Model"
          value={value.liftModel}
          onChange={(e) => onChange({ ...value, liftModel: e.target.value })}
          placeholder="e.g. MonoSpace 500"
          className={inputCls}
        />
      </label>
    </>
  );
}

// ---- Fault-category chip editor ----

export function FaultCategoriesEditor({
  categories,
  onChange,
}: {
  categories: string[];
  onChange: (next: string[]) => void;
}) {
  const [entry, setEntry] = useState("");

  function add() {
    const v = entry.trim();
    if (!v || categories.includes(v)) {
      setEntry("");
      return;
    }
    onChange([...categories, v].slice(0, 20));
    setEntry("");
  }

  return (
    <div>
      <span className={labelCls}>Fault categories</span>
      {categories.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {categories.map((c) => (
            <span key={c} className="inline-flex items-center gap-1">
              <StatusPill word={c} tone="purple" />
              <button
                type="button"
                onClick={() => onChange(categories.filter((x) => x !== c))}
                aria-label={`Remove ${c}`}
                className="text-ele-body hover:text-ele-strong"
              >
                x
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          aria-label="Add a fault category"
          value={entry}
          onChange={(e) => setEntry(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="e.g. door fault, levelling"
          className={inputCls}
        />
        <button
          type="button"
          onClick={add}
          className="max-sm:min-h-11 shrink-0 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
        >
          Add
        </button>
      </div>
    </div>
  );
}

// ---- Tag form (context + document type + fault categories) ----

export interface TagFormState extends ContextValue {
  docType: KnowledgeDocType;
  faultCategories: string[];
}

export function seedTagForm(doc: KnowledgeDocument): TagFormState {
  return {
    siteId: doc.siteId ?? "",
    assetId: doc.assetId ?? "",
    manufacturer: doc.manufacturer ?? "",
    liftModel: doc.liftModel ?? "",
    docType: doc.docType,
    faultCategories: doc.faultCategories,
  };
}

// A tag PATCH body: a blank site/lift/manufacturer/model clears that tag (the server treats null as a
// clear).
export function buildTagPatch(form: TagFormState): KnowledgeTagsPatchInput {
  return {
    siteId: form.siteId || null,
    assetId: form.assetId || null,
    manufacturer: form.manufacturer.trim() || null,
    liftModel: form.liftModel.trim() || null,
    docType: form.docType,
    faultCategories: form.faultCategories,
  };
}

export function DocTypeSelect({
  value,
  onChange,
  idPrefix,
}: {
  value: KnowledgeDocType;
  onChange: (v: KnowledgeDocType) => void;
  idPrefix: string;
}) {
  return (
    <label>
      <span className={labelCls}>Document type</span>
      <select
        id={`${idPrefix}-doctype`}
        aria-label="Document type"
        value={value}
        onChange={(e) => onChange(e.target.value as KnowledgeDocType)}
        className={inputCls}
      >
        {DOC_TYPES.map((t) => (
          <option key={t} value={t}>
            {DOC_TYPE_LABEL[t]}
          </option>
        ))}
      </select>
    </label>
  );
}

// The fields shared by the list modal and the detail-page inline editor.
export function TagFields({
  form,
  onChange,
  idPrefix,
}: {
  form: TagFormState;
  onChange: (next: TagFormState) => void;
  idPrefix: string;
}) {
  return (
    <>
      <ContextFields
        value={form}
        onChange={(ctx) => onChange({ ...form, ...ctx })}
        idPrefix={idPrefix}
      />
      <DocTypeSelect value={form.docType} onChange={(docType) => onChange({ ...form, docType })} idPrefix={idPrefix} />
      <div className="sm:col-span-2">
        <FaultCategoriesEditor categories={form.faultCategories} onChange={(faultCategories) => onChange({ ...form, faultCategories })} />
      </div>
    </>
  );
}

// The list-row tag editor, in a modal.
export function TagEditorModal({
  doc,
  onClose,
  onSaved,
}: {
  doc: KnowledgeDocument;
  onClose: () => void;
  onSaved: (updated: KnowledgeDocument) => void;
}) {
  const [form, setForm] = useState<TagFormState>(() => seedTagForm(doc));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.knowledge.documents.patchTags(doc.id, buildTagPatch(form));
      onSaved(res.document);
    } catch (err) {
      setError(friendlyApiError(err, "Could not save the tags. Please try again."));
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Edit document tags" onClose={() => !busy && onClose()} width="max-w-lg" margin="my-8" panelClassName="p-6">
      <form onSubmit={save}>
        <h2 className="mb-1 font-heading text-2xl tracking-wide text-ele-strong">Edit tags</h2>
        <p className="mb-4 break-all text-sm text-ele-body">{doc.fileName}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <TagFields form={form} onChange={setForm} idPrefix="tag-edit" />
        </div>
        {error && (
          <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {error}
          </p>
        )}
        <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}
          >
            Cancel
          </button>
          <Button type="submit" disabled={busy} className={MODAL_ACTION_BTN}>
            {busy ? "Saving..." : "Save tags"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ---- Two-stage delete (soft, then hard) ----
// The backend deletes in two steps: a soft delete (hides the document from searches) then a hard
// delete (removes the chunks/embeddings and the Drive copy). The soft step is a reversible tier, so
// it is a single confirm; the permanent hard step keeps the platform two-step arming. Shared by the
// list row and the detail page.
export function DocumentDeleteModals({
  doc,
  onClose,
  onDeleted,
}: {
  doc: KnowledgeDocument | null;
  onClose: () => void;
  onDeleted: (id: string) => void;
}) {
  const [stage, setStage] = useState<"soft" | "hard">("soft");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Restart at the first stage whenever the target changes or the flow is dismissed.
    setStage("soft");
    setBusy(false);
    setError(null);
  }, [doc]);

  if (!doc) return null;
  const target = doc;

  // Stage 1 only ARMS: it advances to the permanent-removal confirmation without touching the server,
  // so Cancel at either stage deletes nothing (the earlier design soft-deleted on stage 1, and a
  // Cancel on stage 2 then left the document hidden and unrecoverable). The soft delete and the hard
  // delete both run only on the final confirm, back to back, so the two-endpoint audit trail is
  // preserved but there is no reachable half-deleted state.
  async function removePermanently() {
    setBusy(true);
    setError(null);
    try {
      await api.knowledge.documents.softDelete(target.id);
      await api.knowledge.documents.hardDelete(target.id);
      onDeleted(target.id);
    } catch (err) {
      setError(friendlyApiError(err, "Could not remove this document. Please try again."));
      setBusy(false);
    }
  }

  if (stage === "soft") {
    return (
      <ConfirmDeleteModal
        open
        variant="destructive"
        singleStep
        title="Delete this document?"
        message="This removes the document from the knowledge base. You will confirm the permanent removal on the next step, and nothing is deleted until you do."
        confirmLabel="Continue"
        busy={busy}
        error={error}
        onConfirm={() => setStage("hard")}
        onCancel={() => {
          if (!busy) onClose();
        }}
      />
    );
  }
  return (
    <ConfirmDeleteModal
      open
      variant="destructive"
      title="Permanently remove this document?"
      message="This permanently deletes the document and its search index (chunks and embeddings), and removes the copy filed in Drive. This cannot be undone."
      confirmLabel="Remove permanently"
      busy={busy}
      error={error}
      onConfirm={removePermanently}
      onCancel={() => {
        if (!busy) onClose();
      }}
    />
  );
}

// ==================================================================================================
// Session AFD: query-answer chrome
// ==================================================================================================

// How long a card stays highlighted after a citation marker sends the reader to it.
const CITATION_FLASH_MS = 600;

function prefersReducedMotion(): boolean {
  // jsdom does not implement matchMedia, so this is guarded rather than assumed.
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// The answer block's style sheet is knowledgeUi.css, imported at the top of this file. It was a React
// style element rendered by the Query tab until Session ASB (the Content Security Policy allows no inline
// style element; server/src/config/contentSecurityPolicy.ts), and its header keeps the reasons it is a
// style sheet rather than utilities.

// ---- Source card open/flash state ----
// Held by the Query tab so an inline citation marker and the cards themselves drive one set of state:
// clicking [2] opens card 2, scrolls to it and flashes it, whatever the reader had open before.

export interface KnowledgeSourceCardsApi {
  isOpen: (index: number) => boolean;
  toggle: (index: number) => void;
  reveal: (index: number) => void;
  flashIndex: number | null;
  registerCard: (index: number) => (el: HTMLLIElement | null) => void;
  reset: () => void;
}

export function useKnowledgeSourceCards(): KnowledgeSourceCardsApi {
  const [openIndexes, setOpenIndexes] = useState<number[]>([]);
  const [flashIndex, setFlashIndex] = useState<number | null>(null);
  // The index a citation click asked for. Handled in an effect rather than in the click handler so
  // the scroll happens AFTER the card has been opened (and after the outer sources block has been
  // expanded), when the element actually exists to scroll to.
  const [pending, setPending] = useState<{ index: number } | null>(null);
  const cardRefs = useRef(new Map<number, HTMLLIElement>());
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    [],
  );

  useEffect(() => {
    if (!pending) return;
    const el = cardRefs.current.get(pending.index);
    // Optional-called: jsdom has no scrollIntoView, and a reader who has asked for reduced motion
    // gets a jump rather than a glide.
    el?.scrollIntoView?.({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "center" });
    setFlashIndex(pending.index);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashIndex(null), CITATION_FLASH_MS);
    setPending(null);
  }, [pending]);

  const isOpen = useCallback((index: number) => openIndexes.includes(index), [openIndexes]);

  const toggle = useCallback((index: number) => {
    setOpenIndexes((prev) => (prev.includes(index) ? prev.filter((x) => x !== index) : [...prev, index]));
  }, []);

  const reveal = useCallback((index: number) => {
    setOpenIndexes((prev) => (prev.includes(index) ? prev : [...prev, index]));
    setPending({ index });
  }, []);

  const registerCard = useCallback(
    (index: number) => (el: HTMLLIElement | null) => {
      if (el) cardRefs.current.set(index, el);
      else cardRefs.current.delete(index);
    },
    [],
  );

  // A fresh answer starts with every card collapsed again. The ref map is left alone: React clears
  // the entry for each unmounted card through the ref callback.
  const reset = useCallback(() => {
    setOpenIndexes([]);
    setFlashIndex(null);
    setPending(null);
  }, []);

  return { isOpen, toggle, reveal, flashIndex, registerCard, reset };
}

// ---- Source card ----

export function KnowledgeSourceCard({
  source,
  index,
  open,
  flashing,
  onToggle,
  cardRef,
}: {
  source: KnowledgeQuerySource;
  index: number;
  open: boolean;
  flashing: boolean;
  onToggle: () => void;
  cardRef: (el: HTMLLIElement | null) => void;
}) {
  const bodyId = `kb-source-body-${index}`;
  const [viewerOpen, setViewerOpen] = useState(false);
  const strength = matchStrength(source.similarityScore);
  const equipment = [source.manufacturer, source.liftModel].filter(Boolean).join(" ");

  return (
    <li
      ref={cardRef}
      className={`ele-kb-src-card overflow-hidden rounded-ele border border-ele-hairline bg-ele-inset ${
        flashing ? "ele-kb-src-card--flash" : ""
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex min-h-11 w-full flex-wrap items-center gap-2 px-3 py-2 text-left"
      >
        <StatusPill word={String(index + 1)} tone="purple" />
        <span className="min-w-0 flex-1 break-all text-sm font-semibold text-ele-strong">{source.fileName}</span>
        <StatusPill word={docTypeLabel(source.docType)} tone={docTypePill()} />
        <StatusPill word={source.tier ? tierLabel(source.tier) : matchStrengthLabel(strength)} tone={source.tier ? tierPill(source.tier) : matchStrengthPill(strength)} />
        {/* Page reference only when the document actually has one. A null page is common and correct
            (a Word or text file has no fixed pages), and must never render as a page number. */}
        {source.pageNumber != null && <span className="text-xs text-ele-body">Page {source.pageNumber}</span>}
        <span className="ele-kb-src-toggle-hint font-menu text-xs uppercase tracking-wide text-ele-blue">
          {open ? "Hide passage" : "Show passage"}
        </span>
      </button>

      <div id={bodyId} className={`border-t border-ele-hairline px-3 py-3 ${open ? "" : "ele-kb-src-body--closed"}`}>
        <p className="text-xs text-ele-body">
          {equipment ? `Equipment: ${equipment}` : "No manufacturer or model is recorded on this document."}
        </p>
        {source.documentSummary && <p className="mt-1 text-xs text-ele-body opacity-70">{source.documentSummary}</p>}
        <p className="mt-1 text-xs text-ele-body opacity-70">Passage {source.chunkIndex + 1} of the document.</p>

        <blockquote className="ele-kb-quote ele-kb-src-text mt-2 whitespace-pre-wrap break-words rounded-ele bg-ele-inset-strong py-2 pl-3 pr-2 font-mono text-xs leading-relaxed text-ele-body">
          {source.chunkText}
        </blockquote>

        <div className="mt-2 flex flex-wrap items-center gap-4">
          <Link
            to={`/office/knowledge/${source.documentId}`}
            className="inline-flex min-h-11 items-center font-menu text-xs tracking-wide text-ele-blue underline hover:text-ele-strong"
          >
            View in document
          </Link>
          {/* Session BAV: the page itself (its picture when the platform holds one), in the shared page viewer. Only
              from a BAV server, whose sources always carry hasPageImage; an older reply has no page route behind it. */}
          {source.pageNumber != null && source.hasPageImage !== undefined && (
            <button
              type="button"
              onClick={() => setViewerOpen(true)}
              className="inline-flex min-h-11 items-center font-menu text-xs tracking-wide text-ele-blue underline hover:text-ele-strong"
            >
              {source.hasPageImage ? "Open page picture" : "Open page"}
            </button>
          )}
        </div>
      </div>
      {viewerOpen && source.pageNumber != null && (
        <KnowledgePageViewer
          documentId={source.documentId}
          pageNumber={source.pageNumber}
          onClose={() => setViewerOpen(false)}
          closeLabel="Back to the answer"
        />
      )}
    </li>
  );
}

// The cards render 1:1 with the sources array, in order and never deduplicated: the [N] markers in the
// answer index this exact list, so collapsing two passages from one document would break every marker
// after it.
export function KnowledgeSourceList({
  sources,
  cards,
}: {
  sources: KnowledgeQuerySource[];
  cards: KnowledgeSourceCardsApi;
}) {
  if (sources.length === 0) {
    return <p className="mt-3 text-sm text-ele-body opacity-70">No document sources were cited for this answer.</p>;
  }
  return (
    <ul className="mt-3 space-y-2">
      {sources.map((s, i) => (
        <KnowledgeSourceCard
          key={`${s.documentId}-${s.chunkIndex}-${i}`}
          source={s}
          index={i}
          open={cards.isOpen(i)}
          flashing={cards.flashIndex === i}
          onToggle={() => cards.toggle(i)}
          cardRef={cards.registerCard(i)}
        />
      ))}
    </ul>
  );
}

// ---- The answer, with inline citation markers ----
//
// onCite is what makes a marker clickable. Omit it (as the stored-answer modal does) and sourceCount
// is treated as zero, so every [N] in the text stays ordinary prose: a recorded answer has no source
// cards to point at, and a marker that leads nowhere is worse than no marker at all.
export function KnowledgeAnswer({
  answer,
  sourceCount,
  onCite,
}: {
  answer: string;
  sourceCount: number;
  onCite?: (index: number) => void;
}) {
  const segments = parseAnswerSegments(answer, onCite ? sourceCount : 0);
  return (
    <div className="whitespace-pre-wrap text-sm text-ele-strong">
      {segments.map((seg, i) =>
        seg.kind === "text" ? (
          <span key={i}>{seg.text}</span>
        ) : (
          <button
            key={i}
            type="button"
            onClick={() => onCite?.(seg.sourceIndex)}
            aria-label={`Show source ${seg.label}`}
            className="ele-kb-cite mx-0.5 inline-block rounded px-1.5 py-0.5 font-menu text-xs leading-none"
          >
            {seg.label}
          </button>
        ),
      )}
    </div>
  );
}

// ---- Web search ----

// Shown only for an answer that came from the web ALONE. A knowledge-base answer that was merely
// supplemented from the web is a different thing, and this wording ("no documents found") would be
// untrue of it: the answer-source pill and the web source cards carry that case.
export function KnowledgeWebNotice() {
  return (
    <p role="status" className="ele-kb-web-notice mb-3 rounded-ele px-3 py-2 text-sm">
      {WEB_SEARCH_NOTICE}
    </p>
  );
}

// A web answer can come back with NO citations at all: the model may not have run a search, or it
// searched and matched nothing. Rendering an empty sources area would let that read as a sourced
// answer when it is really the model's own general knowledge, so it is said in the same amber
// treatment as the main notice, in place of the (absent) list. This does NOT replace the main
// notice: both are shown.
export function KnowledgeWebNoSourcesNotice() {
  return (
    <p role="status" className="ele-kb-web-notice mt-3 rounded-ele px-3 py-2 text-sm">
      {WEB_SEARCH_NO_SOURCES_NOTICE}
    </p>
  );
}

export function KnowledgeWebSources({ sources }: { sources: KnowledgeWebSource[] }) {
  if (sources.length === 0) return null;
  return (
    <div className="mt-3 border-t border-ele-hairline pt-3">
      <h3 className="mb-2 font-menu text-xs uppercase tracking-wide text-ele-blue">Web sources ({sources.length})</h3>
      <ul className="space-y-2">
        {sources.map((w, i) => (
          <li key={`${w.url}-${i}`} className="rounded-ele border border-ele-hairline bg-ele-inset px-3 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill word="Web source" tone="amber" />
              <span className="text-xs text-ele-body">{w.domain}</span>
              {w.pageAge && <span className="text-xs text-ele-body opacity-70">{w.pageAge}</span>}
            </div>
            <a
              href={w.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex min-h-11 items-center break-all text-sm font-semibold text-ele-strong underline hover:text-ele-green"
            >
              {w.title || w.url}
            </a>
            {w.snippet && <p className="mt-1 text-xs text-ele-body">{w.snippet}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---- Answer provenance ----

// ---- Needs more context ----
//
// Retrieval was too weak and no equipment was named, so the asker is prompted for the manufacturer and
// model. The office Query tab ALREADY has those two fields in its context picker, so this panel points
// at them and re-runs; it deliberately does not offer a second pair of inputs, which would give the
// same two values two places to live and let them disagree.
export function KnowledgeNeedsContextPanel({
  message,
  onEditEquipment,
  onSearchAgain,
  busy,
}: {
  message: string;
  onEditEquipment: () => void;
  onSearchAgain: () => void;
  busy: boolean;
}) {
  return (
    <div className="rounded-ele border border-ele-hairline bg-ele-inset p-3">
      <p className="whitespace-pre-wrap text-sm text-ele-strong">{message}</p>
      <p className="mt-2 text-xs text-ele-body">
        Fill in Manufacturer and Model in the question form above, then search again.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onEditEquipment}
          className="min-h-11 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
        >
          Add the lift details
        </button>
        <Button onClick={onSearchAgain} disabled={busy} className="min-h-11">
          {busy ? "Thinking..." : "Search again"}
        </Button>
      </div>
    </div>
  );
}
